class SplitBannerReveal extends HTMLElement {
  constructor() {
    super();
    this.timeline = null;
    this.fallbackTimer = null;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFallback();
      return;
    }

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }

    /* Safety fallback — reveal after 5s only if GSAP never initialises */
    this.fallbackTimer = setTimeout(() => this.showFallback(), 5000);
  }

  getGSAP() {
    if (typeof window !== 'undefined' && window.gsap && typeof window.gsap.to === 'function') {
      return window.gsap;
    }
    if (typeof window !== 'undefined' && window.GSAP && typeof window.GSAP.to === 'function') {
      return window.GSAP;
    }
    if (typeof window !== 'undefined' && window.GreenSockGlobals && window.GreenSockGlobals.gsap) {
      return window.GreenSockGlobals.gsap;
    }
    if (typeof gsap !== 'undefined' && typeof gsap.to === 'function') {
      return gsap;
    }
    return null;
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      if (this.getGSAP()) {
        resolve();
        return;
      }

      /* Poll for GSAP availability — CDN defer scripts may not have executed yet */
      var attempts = 0;
      var check = setInterval(() => {
        attempts++;
        if (this.getGSAP() || attempts > 50) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  async init() {
    await this.waitForGSAP();
    const gsapLib = this.getGSAP();

    if (!gsapLib) {
      this.showFallback();
      return;
    }

    /* GSAP loaded — cancel the safety fallback so it doesn't interfere */
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('SplitBanner-' + sectionId);
    if (!section) return;

    var container = section.querySelector('.split-banner__container');
    if (!container) return;

    /* Left side — either image wrapper or text content */
    var leftSide = section.querySelector('.split-banner__image-wrapper')
                || section.querySelector('.split-banner__left-content');
    /* Right side — always the content block */
    var rightSide = section.querySelector('.split-banner__content');

    if (!leftSide && !rightSide) {
      this.showFallback();
      return;
    }

    var hasScrollTrigger = typeof ScrollTrigger !== 'undefined' ||
                          (gsapLib.plugins && gsapLib.plugins.scrollTrigger);

    if (!hasScrollTrigger) {
      /* Without ScrollTrigger, scrub-based animation is impossible — just show content */
      this.showFallback();
      return;
    }

    if (gsapLib.registerPlugin) {
      try {
        gsapLib.registerPlugin(ScrollTrigger || gsapLib.plugins.scrollTrigger);
      } catch (e) {
        // ScrollTrigger already registered
      }
    }

    /* Detect mobile (stacked layout) */
    var isMobile = window.innerWidth < 750;

    /*
     * Build a single timeline scrubbed by scroll position.
     * start  = section top hits 90% of viewport (just entering view)
     * end    = section top hits 30% of viewport (well into view)
     * scrub  = 0.6 for smooth interpolation (not jerky)
     *
     * Scrolling down  → plays the timeline forward  → sides slide together
     * Scrolling back  → plays the timeline backward → sides slide apart
     */
    this.timeline = gsapLib.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top 90%',
        end: 'top 30%',
        scrub: 0.6
      }
    });

    /* Animate left side — slide in from the left (or bottom on mobile) */
    if (leftSide) {
      if (isMobile) {
        this.timeline.fromTo(leftSide,
          { opacity: 0, y: 60 },
          { opacity: 1, y: 0, duration: 1, ease: 'none' },
          0 /* start at the same time */
        );
      } else {
        this.timeline.fromTo(leftSide,
          { opacity: 0, x: -100 },
          { opacity: 1, x: 0, duration: 1, ease: 'none' },
          0
        );
      }
    }

    /* Animate right side — slide in from the right (or bottom on mobile) */
    if (rightSide) {
      if (isMobile) {
        this.timeline.fromTo(rightSide,
          { opacity: 0, y: 60 },
          { opacity: 1, y: 0, duration: 1, ease: 'none' },
          0
        );
      } else {
        this.timeline.fromTo(rightSide,
          { opacity: 0, x: 100 },
          { opacity: 1, x: 0, duration: 1, ease: 'none' },
          0
        );
      }
    }
  }

  showFallback() {
    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('SplitBanner-' + sectionId);
    if (!section) return;

    if (this.timeline && this.timeline.kill) {
      this.timeline.kill();
    }

    var els = section.querySelectorAll(
      '.split-banner__image-wrapper, .split-banner__left-content, .split-banner__content'
    );
    els.forEach(function(el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  disconnectedCallback() {
    if (this.timeline) {
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      if (this.timeline.kill) this.timeline.kill();
    }
  }
}

if (!customElements.get('split-banner-reveal')) {
  customElements.define('split-banner-reveal', SplitBannerReveal);
}
